import React, { useState, useMemo } from "react";
import { Box, Chip, Typography, Tooltip } from "@mui/material";

interface RenderSelectedFiltersProps {
  filters: { [key: string]: { [key: string]: boolean } };
  tasks: { id: string | number; name: string }[];
  maxVisible?: number;
  showAllInitially?: boolean;
  showTitle?: boolean;
  title?: string;
  showCloseBtn?: boolean;
  onDelete?: (tabKey: string, value: string) => void;
  formatRentalValueLabel?: (range: string) => string;
  formatContractSinceLabel?: (value: string) => string; // 👈 new prop
}

const RenderSelectedFilters: React.FC<RenderSelectedFiltersProps> = ({
  filters,
  tasks,
  maxVisible = 5,
  showAllInitially = false,
  showTitle = true,
  title = "Selected Filters",
  showCloseBtn = true,
  onDelete,
  formatRentalValueLabel = (range: string) => range,
  formatContractSinceLabel = (value: string) => value, // 👈 default passthrough
}) => {
  const [showAll, setShowAll] = useState(showAllInitially);

  // Normalize case to match API response (e.g., "delhi" -> "Delhi")
  const normalizeCase = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
console.log(filters, "filtersfiltersfiltersfilters")
  // Flatten and deduplicate selected tags
  const selectedTags = useMemo(() => {
  const tags = Object.keys(filters).flatMap((tabKey) => {
    if (tabKey === "taskNames") {
      return Object.entries(filters[tabKey])
        .filter(([, isChecked]) => isChecked)
        .map(([filterKey]) => {
          const task = tasks.find((t) => t.id.toString() === filterKey);
          return {
            tabKey,
            value: filterKey,
            label: task ? normalizeCase(task.name) : normalizeCase(filterKey),
          };
        });
    }

    if (tabKey === "locations") {
      return Object.entries(filters[tabKey])
        .filter(([, isChecked]) => isChecked)
        .map(([filterKey]) => ({
          tabKey,
          value: filterKey,
          label: filterKey.toUpperCase(), // ✅ always uppercase in locations
        }));
    }

    if (tabKey === "Current_rental_value") {
      return Object.entries(filters[tabKey])
        .filter(([, isChecked]) => isChecked)
        .map(([filterKey]) => ({
          tabKey,
          value: filterKey,
          label: formatRentalValueLabel(filterKey),
        }));
    }

    if (tabKey === "Contract_since") {
      return Object.entries(filters[tabKey])
        .filter(([, isChecked]) => isChecked)
        .map(([filterKey]) => ({
          tabKey,
          value: filterKey,
          label: formatContractSinceLabel(filterKey),
        }));
    }

    // ✅ fallback (covers outlet_type, region, status, etc.)
    return Object.entries(filters[tabKey])
      .filter(([, isChecked]) => isChecked)
      .map(([filterKey]) => {
        // check if this filterKey belongs to locations
        const isLocation =
          Object.keys(filters.locations || {}).some(
            (loc) => loc.toLowerCase() === filterKey.toLowerCase()
          );

        return {
          tabKey,
          value: filterKey,
          label: isLocation ? filterKey.toUpperCase() : normalizeCase(filterKey),
        };
      });
  });

  // Deduplicate case-insensitively
  return Array.from(new Set(tags.map((tag) => tag.label.toLowerCase()))).map(
    (lowerTag) => {
      const tag = tags.find((t) => t.label.toLowerCase() === lowerTag) || tags[0];
      return tag;
    }
  );
}, [filters, tasks, formatRentalValueLabel, formatContractSinceLabel]);


  // Toggle visibility
  const toggleShowAll = () => setShowAll((prev) => !prev);
console.log(selectedTags, "selectedTagsselectedTagsselectedTags")
  return (
    <Box>
      {showTitle && selectedTags.length > 0 && (
        <Typography
          variant="subtitle1"
          sx={{ marginBottom: "8px", color: "#282829" }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {selectedTags
          .slice(0, showAll ? selectedTags.length : maxVisible)
          .map((tag, index) => (
            <Chip
              key={`${tag.tabKey}-${tag.value}-${index}`}
              label={tag.label}
              onDelete={
                showCloseBtn ? () => onDelete?.(tag.tabKey, tag.value) : undefined
              }
              sx={{
                backgroundColor: "#EAF5FE",
                color: "#0F7CAD",
                borderRadius: "5px",
                fontSize: "14px",
                height: "30px",
                "& .MuiChip-deleteIcon": {
                  color: "#007bff",
                  fontSize: "18px",
                },
              }}
              deleteIcon={
                showCloseBtn ? (
                  <span style={{ fontWeight: "bold" }}>×</span>
                ) : undefined
              }
            />
          ))}

        {/* Show More / Less Toggle */}
        {selectedTags.length > maxVisible && (
          <Tooltip
            title={
              showAll
                ? ""
                : selectedTags.slice(maxVisible).map((tag) => tag.label).join(", ")
            }
            arrow
            sx={{
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#333",
                color: "#fff",
                fontSize: "14px",
                borderRadius: "4px",
              },
              "& .MuiTooltip-arrow": {
                color: "#333",
              },
            }}
          >
            <Chip
              label={
                showAll
                  ? "Show Less"
                  : `More +${selectedTags.length - maxVisible}`
              }
              onClick={toggleShowAll}
              sx={{
                backgroundColor: "#fff",
                color: "#C8172F",
                borderRadius: "5px",
                fontSize: "14px",
                height: "30px",
                cursor: "pointer",
                "&:hover": {
                  background: "#f5f5f5",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default RenderSelectedFilters;
